import { useState, forwardRef } from 'react';

import type { VariantProps } from 'tailwind-variants';
import { tv } from 'tailwind-variants';

import { cn } from '@avodah-inc/ui/utils';

import { RadioGroup, Surface, ToggleSwitch, Typography } from '../../atoms';

const radioCardVariants = tv({
  base: 'flex w-[14.813rem] h-52',
  variants: {
    variant: {
      default: 'text-gray-900 rounded',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

export type RadioCardProps = React.HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof radioCardVariants> & {
    headerText: string;
    options: { value: string; label: string }[];
    disabled?: boolean;
  };

export const RadioCard = forwardRef<HTMLDivElement, RadioCardProps>(
  ({ className, variant, ...props }, ref) => {
    const [isActive, setIsActive] = useState(false);
    const [value, setValue] = useState<string | undefined>('');

    return (
      <div
        className={cn(
          radioCardVariants({
            variant,
            className,
          }),
        )}
        ref={ref}
        {...props}
      >
        <Surface
          data-state={isActive ? 'checked' : 'unchecked'}
          elevation={0}
          className="data-[state=checked]:border-primary-300 h-full w-full p-4 hover:shadow-none"
        >
          <div className="flex h-full w-full flex-col gap-y-3">
            <div className="flex flex-row justify-between">
              <Typography variant="h4">{props.headerText}</Typography>
              <ToggleSwitch
                disabled={props.disabled}
                onCheckedChange={(state: boolean) => {
                  setIsActive(state);
                  if (!state) setValue(undefined);
                }}
              />
            </div>
            <div className="h-[1px] w-full bg-gray-300" />
            {/* // Map through these */}
            <RadioGroup
              disabled={!isActive}
              className="flex flex-col items-start gap-y-2"
              onValueChange={(v) => {
                setValue(v);
              }}
              value={value}
              options={props.options}
            />
          </div>
        </Surface>
      </div>
    );
  },
);

RadioCard.displayName = 'RadioCard';
